import { useMemo } from 'react';
import { Box, DataTable, type DataTableFeatures } from '@customerio/pluma-components/react';
import type { ColumnDef } from '@tanstack/react-table';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { DataIndexTableFrame, useSearchDraft } from '../components/table-shell';
import type { Conversion } from './adapter';
import {
  ConversionCountCell,
  ConversionCountedWhenCell,
  ConversionImpactCell,
  ConversionNameCell,
  ConversionsCountNote,
  ConversionUsageCell,
  type ConversionUsageTerms,
} from './cells';
import { CONVERSIONS_PAGE_SIZE } from './rows';
import { goalPath } from './urls';

/**
 * The workspace's conversions, one row per goal.
 *
 * The layout and copy are the spike's (`customerio/ui#16717`,
 * `app/components/data-index-conversions-table`); the rows come from the goals
 * list rather than from definitions built in the browser, so the table itself
 * neither groups nor de-duplicates anything.
 */

/** How many days the count column covers, once AUD-4576 fills it in. */
export const CONVERSION_COUNT_DAYS = 30;

export type ConversionsTableProps = {
  workspaceId: number;
  /** The rows on the current page, already searched and paged. */
  rows: Conversion[];
  /** How many rows the search left, which is what the pager counts. */
  total: number;
  isLoading: boolean;
  search: string;
  onSearchChange: (search: string) => void;
  page: number;
  onPageChange: (page: number) => void;
};

export function ConversionsTable({
  workspaceId,
  rows,
  total,
  isLoading,
  search,
  onSearchChange,
  page,
  onPageChange,
}: ConversionsTableProps) {
  const taxonomy = useProductTaxonomy();
  const draft = useSearchDraft(search);

  const terms = useMemo(
    () => ({
      campaignSingular: taxonomy.term('campaigns.singular'),
      newsletterSingular: taxonomy.term('broadcasts.singular'),
    }),
    [taxonomy],
  );
  const columns = useConversionColumns(String(workspaceId), terms);
  // Both nouns are renameable, so they are read rather than written: a
  // workspace that calls campaigns something else would otherwise be told to
  // set a conversion on a thing it does not have.
  const emptyStateDescription = `Conversions are the goals this workspace tracks. Set one on ${taxonomy.term(
    'campaigns.indefiniteSingular',
  )} or ${taxonomy.term('broadcasts.indefiniteSingular')}, or create one from the Goals page.`;

  return (
    <DataTable
      caption="Conversions"
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      isLoading={isLoading}
      withSearch
      search={{
        value: search,
        inputValue: draft.text,
        onSearchInput: draft.setText,
        onSearch: onSearchChange,
        shouldSearchOnEnter: true,
        shouldSearchOnClear: true,
        isManual: true,
      }}
      withPagination
      pagination={{
        page,
        pageSize: CONVERSIONS_PAGE_SIZE,
        totalItemCount: total,
        onPageChange,
        isManual: true,
      }}
      emptyState={{
        title: 'No conversions yet',
        description: emptyStateDescription,
        icon: 'data-index',
      }}
    >
      <DataIndexTableFrame />
      <Box mt="200" px="150">
        <ConversionsCountNote days={CONVERSION_COUNT_DAYS} />
      </Box>
    </DataTable>
  );
}

function useConversionColumns(
  workspaceId: string,
  terms: ConversionUsageTerms,
): ColumnDef<DataTableFeatures, Conversion>[] {
  return useMemo(
    () => [
      {
        id: 'name',
        header: 'Conversion',
        enableHiding: false,
        cell: ({ row }) => (
          <ConversionNameCell conversion={row.original} href={goalPath(workspaceId, row.original.id)} />
        ),
      },
      {
        id: 'counted-when',
        header: 'Counted when',
        maxSize: 260,
        cell: ({ row }) => <ConversionCountedWhenCell conversion={row.original} />,
      },
      {
        id: 'conversions',
        header: `Conversions (last ${String(CONVERSION_COUNT_DAYS)} days)`,
        maxSize: 220,
        cell: () => <ConversionCountCell />,
      },
      {
        id: 'impact',
        header: 'Impact',
        maxSize: 170,
        cell: ({ row }) => (
          <ConversionImpactCell conversion={row.original} href={goalPath(workspaceId, row.original.id)} />
        ),
      },
      {
        id: 'sources',
        header: 'Used by',
        maxSize: 160,
        cell: ({ row }) => <ConversionUsageCell conversion={row.original} terms={terms} />,
      },
    ],
    [terms, workspaceId],
  );
}
